import * as Yup from 'yup';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

// ----------------------------------------------------------------------

// Phone fields only accept US / Canada numbers: "+1" followed by exactly 10 digits.
// To support another country later, add it here (all listed countries must share PHONE_DIAL_CODE).
export const PHONE_COUNTRIES = ['us', 'ca'];

export const PHONE_DIAL_CODE = '1';

export const PHONE_NATIONAL_LENGTH = 10;

const DIAL_CODES = new Set(getCountries().map((country) => getCountryCallingCode(country)));

// RHFPhoneField keeps the selected country's dial code (e.g. "+44") in the value,
// so a bare dial code means no number was typed.
export function isEmptyPhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return !digits || DIAL_CODES.has(digits);
}

/**
 * Normalizes any stored/typed phone value to E.164 (e.g. "+15551234567", "+447911123456").
 * Values with a "+" keep their own country. Legacy values saved without one
 * ("555-123-4567", "(555) 123 4567") are read as `defaultCountry` numbers.
 */
export function normalizePhone(value, defaultCountry = 'US') {
  if (isEmptyPhone(value)) return '';

  const raw = String(value).trim();
  const parsed = raw.startsWith('+')
    ? parsePhoneNumberFromString(raw)
    : parsePhoneNumberFromString(raw, defaultCountry) || parsePhoneNumberFromString(`+${raw.replace(/\D/g, '')}`);

  if (parsed) return parsed.number;

  const digits = raw.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

export function isValidPhone(value) {
  if (!value) return false;

  const parsed = parsePhoneNumberFromString(value);

  return (
    !!parsed?.isValid() &&
    parsed.countryCallingCode === PHONE_DIAL_CODE &&
    parsed.nationalNumber.length === PHONE_NATIONAL_LENGTH
  );
}

/**
 * Reusable Yup rule for phone fields rendered with RHFPhoneField.
 * @example phone: phoneSchema()                 // optional
 * @example phone: phoneSchema({ required: true })
 */
export function phoneSchema({ required = false, message = 'Invalid phone number' } = {}) {
  return Yup.string()
    .trim()
    .test('is-valid-phone', message, function validate(value) {
      if (isEmptyPhone(value)) {
        return !required || this.createError({ message: 'Phone number is required' });
      }
      return isValidPhone(value);
    });
}
